import * as Yup from 'yup'

// Basic validation rules
export const validationRuleSimpleEmail = Yup.string().email(
  'Veuillez inscrire une adresse courriel valide du type exemple@nomdedomaine.ca',
)

export const validationRuleEmail = Yup.string()
  .email('Veuillez inscrire une adresse courriel valide du type exemple@nomdedomaine.ca')
  .required('Veuillez inscrire une adresse courriel valide du type exemple@nomdedomaine.ca')

export const validationRuleFieldPhone = Yup.string().matches(/^\d{3} \d{3}-\d{4}$/, {
  message:
    'Veuillez inscrire un numéro de téléphone en respectant le format suivant : 123 123-1234',
  excludeEmptyString: true,
})

export const validationRuleFirstNameField = Yup.string().required('Veuillez inscrire votre prénom')
export const validationRuleLastNameField = Yup.string().required(
  'Veuillez inscrire votre nom de famille',
)
export const validationRuleTitleField = Yup.string().required(
  'Veuillez faire un choix dans la liste déroulante',
)
// Titres multiples (titre_c est un choix multiple dans le CRM) : au moins un titre.
export const validationRuleTitlesField = Yup.array()
  .of(Yup.string())
  .min(1, 'Veuillez faire un choix dans la liste déroulante')

// Required phone validation (for main phone)
const createRequiredPhoneSchema = () => {
  return Yup.object().shape({
    phone_number: validationRuleFieldPhone.required(
      'Veuillez inscrire un numéro de téléphone en respectant le format suivant : 123 123-1234',
    ),
    type: Yup.string().required('Obligatoire'),
    phone_number_post: Yup.string().nullable().max(6, 'Le champ Poste doit contenir un maximum de 6 chiffres'),
  })
}

// Optional phone validation (for other phones)
const createOptionalPhoneSchema = () => {
  return Yup.object().shape({
    phone_number: Yup.string().test(
      'optional-phone-format',
      'Veuillez inscrire un numéro de téléphone en respectant le format suivant : 123 123-1234',
      function (value) {
        // If no phone number, validation passes
        if (!value || value.trim() === '') {
          return true
        }
        // If phone number exists, validate format
        return /^\d{3} \d{3}-\d{4}$/.test(value)
      }
    ),
    type: Yup.string().test(
      'optional-type-required',
      'Obligatoire',
      function (value) {
        const phoneNumber = this.parent.phone_number
        // If no phone number, type not required
        if (!phoneNumber || phoneNumber.trim() === '') {
          return true
        }
        // If phone number exists, type is required
        return Boolean(value && value.trim() !== '')
      }
    ),
    phone_number_post: Yup.string().nullable().max(6, 'Le champ Poste doit contenir un maximum de 6 chiffres'),
  })
}

// Address validation schema
const createAddressValidationSchema = () => {
  return Yup.object().shape({
    street: Yup.string().required('Obligatoire'),
    office: Yup.string(), // Optional field - no asterisk in form
    country: Yup.string().required('Obligatoire'),
    civic_number: Yup.string().required('Obligatoire'),
    city: Yup.string().when('province', {
      is: (value: string) => value !== 'QC',
      then: () => Yup.string().required('Veuillez saisir une ville'),
      otherwise: () => Yup.string().nullable(),
    }),
    city_id: Yup.string().when('province', {
      is: 'QC',
      then: () => Yup.string().required('Veuillez saisir une ville'),
      otherwise: () => Yup.string().nullable(),
    }),
    postal_code: Yup.string().required('Veuillez saisir un code postal'),
    province: Yup.string().required('Obligatoire'),
  })
}

// Personal Information Schema (replaces personalInformationSchema.ts)
export const createPersonalInformationSchema = (isMember: boolean) => {
  const baseSchema = {
    first_name: validationRuleFirstNameField,
    last_name: validationRuleLastNameField,
    email: validationRuleEmail,
    mainPhone: createRequiredPhoneSchema(),
    otherPhones1: createOptionalPhoneSchema(), // Optional
    otherPhones2: createOptionalPhoneSchema(), // Optional
    address: createAddressValidationSchema(),
  }

  const memberSchema = {
    recovery_email: validationRuleSimpleEmail.test(
      'not-equal',
      'L\'adresse courriel de récupération doit être différente de l\'adresse courriel principale',
      function (value) {
        // Recovery email is optional, only validate if provided
        if (!value || value.trim() === '') {
          return true
        }
        const { email } = this.parent
        return value !== email
      },
    ),
    titles: validationRuleTitlesField,
  }

  return Yup.object().shape({
    ...baseSchema,
    ...(isMember ? memberSchema : {}),
  })
}

// Legacy schemas (keeping for backward compatibility)
export const validationRulePhoneComponent = Yup.object().shape({
  phone_number: validationRuleFieldPhone.required(
    'Veuillez inscrire un numéro de téléphone en respectant le format suivant : 123 123-1234',
  ),
  type: Yup.string().required('Obligatoire'),
  phone_number_post: Yup.string().nullable(),
})
export const contactSchema = Yup.object().shape({
  first_name: Yup.string().required('Veuillez inscrire votre prénom'),
  last_name: Yup.string().required('Veuillez inscrire votre nom de famille'),
  email: validationRuleEmail,
  mainPhone: Yup.object().shape({
    phone_number: validationRuleFieldPhone.required(
      'Veuillez inscrire un numéro de téléphone en respectant le format suivant : 123 123-1234',
    ),
    type: Yup.string().required('Obligatoire'),
    phone_number_post: Yup.string().nullable().max(6, 'Le champ Poste doit contenir un maximun de 6 chiffres'),
  }),
  otherPhone: Yup.object().shape({
    phone_number: validationRuleFieldPhone,
    type: Yup.string(),
    phone_number_post: Yup.string().nullable().max(6, 'Le champ Poste doit contenir un maximun de 6 chiffres'),
  }),
})
export const userMemberSchema = Yup.object().shape({
  first_name: Yup.string().required('Veuillez inscrire votre prénom'),
  last_name: Yup.string().required('Veuillez inscrire votre nom de famille'),
  email: validationRuleEmail,
  contact: Yup.object().shape({
    phone_number: validationRuleFieldPhone.required(
      'Veuillez inscrire un numéro de téléphone en respectant le format suivant : 123 123-1234',
    ),
    phone_number_post: Yup.string()
      .nullable()
      .max(6, 'Ce champ ne peut contenir que des chiffres (maximum 6)'),
  }),
  titles: Yup.array().of(Yup.string()).min(1, 'Obligatoire'),
})

export const validationRuleFoundingYear = Yup.number()
  .integer('L\'année doit être un nombre entier')
  .min(1000, 'L\'année doit être au minimum 1000')
  .max(new Date().getFullYear() + 1, `L'année ne peut pas être supérieure à ${new Date().getFullYear() + 1}`)
  .typeError('Veuillez entrer une année valide')

export const userEnterpriseSchema = Yup.object().shape({
  inviteList: Yup.array().of(
      Yup.object().shape({
        email: validationRuleEmail,
        first_name: Yup.string().required('Veuillez inscrire le prénom'),
        last_name: Yup.string().required('Veuillez inscrire le nom de famille'),
        roles: Yup.array().min(1, 'Au moins un rôle est requis')
      })
  )
});